import * as React from "react";

/**
 * Motion primitives for the marketing site.
 *
 * The console does not use these. A dashboard that animates on scroll is a
 * dashboard that makes you wait to read a number, and the people using it are
 * looking at it for the fourth time today.
 */

/**
 * Whether the visitor has asked for less motion.
 *
 * Read as a hook rather than left to a CSS media query because the animations
 * here are driven by JavaScript state, not by keyframes - a `@media` block
 * cannot stop a `requestAnimationFrame` loop from spinning, and that loop is the
 * expensive part. Components branch on this to render the *finished* state
 * rather than a frozen first frame: someone who turned motion off should see the
 * completed assessment, not an empty phone.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(() =>
    typeof window === "undefined"
      ? false
      : (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  );

  React.useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * True once the element has been scrolled into view - and it stays true.
 *
 * One-shot on purpose. A reveal that re-runs every time a section scrolls back
 * past is the effect people mean when they say a site feels like it is fighting
 * them; the content has already been read by then, and playing it in again just
 * hides it.
 */
export function useInView<T extends Element>(options?: IntersectionObserverInit) {
  const ref = React.useRef<T>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    // No IntersectionObserver means an old browser, not a reason to hide the
    // page's content - show everything and skip the effect.
    if (!element || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05, ...options }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);

  return { ref, inView };
}

/**
 * A looping clock in seconds, driven by `requestAnimationFrame`.
 *
 * Not `setInterval`. An interval keeps firing in a background tab and drifts
 * against real time; rAF is paused by the browser when the tab is hidden and
 * hands back a timestamp, so the playhead is always derived from elapsed time
 * rather than accumulated from a tick count.
 *
 * `speed` exists because the thing being replayed genuinely takes ninety
 * seconds, and nobody watches a landing page for ninety seconds. The surface
 * that renders this has to say so - an animation implying the whole assessment
 * takes fifteen seconds would undersell the product by lying about it.
 */
export function useLoopingClock({
  duration,
  speed = 1,
  running = true,
  /** Seconds of stillness on the last frame before it starts over. */
  holdAtEnd = 0,
}: {
  duration: number;
  speed?: number;
  running?: boolean;
  holdAtEnd?: number;
}): number {
  const [elapsed, setElapsed] = React.useState(0);
  // Where the loop resumes from, so pausing and playing does not jump.
  const offsetRef = React.useRef(0);

  React.useEffect(() => {
    if (!running) return;

    let frame = 0;
    let startedAt: number | null = null;
    const cycle = duration + holdAtEnd * speed;

    function tick(now: number) {
      startedAt ??= now;
      const played = ((now - startedAt) / 1000) * speed + offsetRef.current;
      const position = played % cycle;

      offsetRef.current = position;
      startedAt = now;
      setElapsed(Math.min(position, duration));
      frame = requestAnimationFrame(tick);
    }

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [duration, speed, running, holdAtEnd]);

  return elapsed;
}
